import React, { createContext, useContext, useState, useCallback } from "react"
import type { AuthUser } from "../constants/types"

type AuthContextType = {
  user: AuthUser | null
  setUser: (user: AuthUser) => void
  clearUser: () => void
}

const AUTH_STORAGE_KEY = "auth_user"

function getStoredUser(): AuthUser | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<AuthUser | null>(getStoredUser)

  const setUser = useCallback((user: AuthUser) => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user))
    setUserState(user)
  }, [])

  const clearUser = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY)
    setUserState(null)
  }, [])

  return <AuthContext.Provider value={{ user, setUser, clearUser }}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
